import { SystemTaskResponseDTO, SystemTaskResultDTO } from 'business/models/Models';
import RMSvgIcon, {
  RMSvgIconNames,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import React, { useEffect, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { removeTask, updateProgress } from 'redux/reducers/systemTaskReducer';
import { IStyle } from 'Styles';
import { isNullOrUndefined } from 'utils/Global';
import { PostCompletionAction } from './PostCompletionAction';

/**
 * Represents a long-running system task that executes an async service method,
 * tracks its progress, and optionally displays post-completion actions.
 */
export type LongRunningTask = {
  /** Unique identifier for the task */
  id: number;
  /** Display title of the task */
  title: string;
  /** Parameters to be passed to the service method */
  param: any[];
  /** The async service method that performs the task operation */
  serviceMathod: (param: any, taskId: number) => Promise<SystemTaskResponseDTO>;
  /** Optional callback triggered after task completion; may return false to indicate the task did not complete successfully */
  onComplete?: (result: any) => boolean | void;
  /** Current task progress percentage */
  progress?: number;
  /** List of post-completion actions (buttons or operations to trigger) */
  postActions?: PostTaskAction[];
  /** Properties of Icon for task to display */
  iconProps?: IconProps;
  /** Optional callback executed when the task fails */
  onError?: any;
  /** Title to display when task completes successfully */
  completionTitle?: string;
};

export type PostTaskAction = {
  iconProps: IconProps;
  method: (param: any, actionResult: any) => void;
  param: any;
  titleTip: string;
};

type IconProps = {
  name: RMSvgIconNames;
  fill: string;
};

export const SystemTask = (task: LongRunningTask) => {
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState({ error: false, message: '' });
  const [actionResult, setActionResult] = useState<SystemTaskResultDTO>();
  const dispatch = useRMDispatch();

  useEffect(() => {
    task
      .serviceMathod(task.param, task.id)
      .then(onTaskFinish)
      .catch((e) => {
        dispatch(updateProgress({ taskId: task.id, progress: 100 }));
        setError({ error: true, message: e.message ?? e.ErrorMessage });
        task.onError && task.onError({ message: e.message ?? e.ErrorMessage });
        setCompleted(true);
      });
  }, []);

  function onTaskFinish(result: SystemTaskResponseDTO) {
    dispatch(updateProgress({ taskId: task.id, progress: 100 }));
    if (result.success === true) {
      setActionResult(result.result);
      if (task.onComplete) {
        const success = task.onComplete(result.result);
        if (success === false) {
          setError({ error: true, message: 'Task did not complete successfully.' });
        }
      }
    } else {
      setError({ error: true, message: result.message });
      task.onError && task.onError(result);
    }
    setCompleted(true);
  }

  function closeClickHandler(e) {
    dispatch(removeTask(task.id));
  }

  return (
    <div id="systemtask-wrapper" style={styles.taskContainer}>
      <div
        id={`systemtask-${task.title.toLocaleLowerCase()}-container`}
        className="taskStyle"
      >
        {!isNullOrUndefined(task.iconProps) && (
          <RMSvgIcon id={`systemtask-${task.id}-icon`} {...task.iconProps} />
        )}
        <div
          id={`systemtask-${task.title.toLocaleLowerCase()}-row`}
          style={styles.taskRow}
        >
          {completed == true && (
            <div
              id={`systemtask-${task.title.toLocaleLowerCase()}-title-completed`}
              className="taskTitle"
            >
              {error.error ? task.title : task.completionTitle ?? task.title}
            </div>
          )}
          {completed == false && (
            <>
              <div
                id={`systemtask-${task.title.toLocaleLowerCase()}-title-running`}
                className="taskTitle"
              >
                {' '}
                {task.title}{' '}
              </div>
              <div
                id={`systemtask-${task.title.toLocaleLowerCase()}-progress`}
                className="taskProgress position-relative w-100pr h-10"
              >
                <div
                  id={`systemtask-${task.title.toLocaleLowerCase()}-progress-bar`}
                  className={
                    'progress-bar' + (error.error == true ? ' bg-danger' : ' bg-success')
                  }
                  role="progressbar"
                  aria-valuenow={task.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  style={{ width: `${task.progress}%` }}
                ></div>
              </div>
            </>
          )}
        </div>
        <div id={`systemtask-${task.title.toLocaleLowerCase()}-postaction-wrapper`}>
          {completed == true && (
            <PostCompletionAction
              actionResult={actionResult}
              closeClickHandler={closeClickHandler}
              actions={task.postActions}
            />
          )}
        </div>
      </div>
      {error.error == true && (
        <div id={`systemtask-postaction-errormessage`} className="text-danger">
          {error ? error.message : 'Failed'}
        </div>
      )}
    </div>
  );
};

const styles: IStyle = {
  taskContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0.5rem',
  },
  taskRow: {
    display: 'flex',
    flexDirection: 'column',
    flex: '90%',
  },
};
